import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Box, Button, Card, IconButton, Switch, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import useSystemsStyles from './useSystemsStyles';
import { AddFilled, DeleteRegular, VideoFilled } from '@fluentui/react-icons';
import { ConfirmDialog, SystemDialog } from '@ui/components/Dialogs';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { deleteAssetSystem, resetSystemEditMode, setIsSystemUpdated, updateSystem } from '@application/redux/SystemDetail/system-detail-slice';
import { clearSystems, fetchSystems } from '@application/redux/SystemDetail/system-detail-slice';
import { toast } from 'react-toastify';
const Systems = (props) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { systems, isSystemUpdated } = useSelector((state) => state.systemDetail);
    const { systemsCardStyles } = useSystemsStyles();
    const pathname = window.location.pathname;
    const segments = pathname.split('/');
    const id = segments[segments.length - 1];
    const [systemDialog, setSystemDialog] = useState({
        show: false,
        variant: ''
    });
    const [isEdit, setIsEdit] = useState(false);
    const [selectedSystemId, setSelectedSystemId] = useState(null);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [systemUpdateTrigger, setSystemUpdateTrigger] = useState(0);
    const handleSystemUpdated = (updatedSystem) => {
        console.log('System updated callback called, triggering refresh...');
        setSystemUpdateTrigger((prev) => prev + 1);
    };
    const handleDelete = async () => {
        setIsDeleting(true);
        if (itemToDelete) {
            try {
                var resultAction = await dispatch(deleteAssetSystem(itemToDelete.id));
                if (deleteAssetSystem.fulfilled.match(resultAction)) {
                    const { isSuccess, message } = resultAction.payload;
                    if (isSuccess) {
                        dispatch(fetchSystems(id));
                        toast.success(message || 'System created successfully!');
                    }
                }
                else if (deleteAssetSystem.rejected.match(resultAction)) {
                    toast.error(resultAction.payload);
                }
            }
            catch (error) {
                toast.error('An unexpected error occurred while creating the system.');
            }
            finally {
                setIsDeleting(false);
                setIsConfirmDialogOpen(false);
            }
        }
    };
    useEffect(() => {
        dispatch(fetchSystems(id));
        return () => {
            dispatch(clearSystems());
        };
    }, [systemDialog.show, isSystemUpdated, systemUpdateTrigger]);
    const handleEditSystem = async (data) => {
        try {
            const response = await dispatch(updateSystem(data));
            if (updateSystem.fulfilled.match(response)) {
                toast.success('System status updated successfully');
                dispatch(setIsSystemUpdated());
                dispatch(resetSystemEditMode());
            }
        }
        catch (error) {
            toast.error('System status updation error');
        }
    };
    const rows = systems?.data?.data.map((s) => ({
        id: s.id || '',
        systemName: s.systemName,
        systemType: s.systemType || '',
        description: s.description,
        isActive: s.isActive,
        assetDetailId: s.assetDetailId,
        cameraUrl: s.cameraUrl,
        jetsonId: s.jetsonId,
        systemId: s.systemId,
        status: s.status,
        isDeleted: s.isDeleted,
        asset: s.asset,
        lastLocation: s.lastLocation,
        lastInfoReceived: s.lastInfoReceived,
        cameraStatus: s.cameraStatus,
        liveStreamUrl: s.liveStreamUrl
    })) || [];
    const columns = [
        {
            field: 'isActive',
            headerName: '',
            width: 60,
            type: 'boolean',
            align: 'center',
            editable: false,
            sortable: false,
            renderCell: (params) => {
                var currentSystem = systems?.data?.data?.filter((row) => row.id === params.id);
                if (!currentSystem || currentSystem.length === 0)
                    return null;
                return (_jsx(Switch, { onChange: (e) => {
                        const updatedRow = {
                            ...params.row,
                            isActive: e.target.checked,
                            systemId: currentSystem ? currentSystem[0].systemId : null,
                            ActiveInactive: true
                        };
                        handleEditSystem(updatedRow);
                    }, checked: params.value, size: 'small', color: 'primary' }));
            }
        },
        {
            field: 'systemName',
            headerName: t('SystemName'),
            minWidth: 198,
            flex: 1,
            editable: false
        },
        {
            field: 'systemType',
            headerName: t('SystemType'),
            minWidth: 198,
            flex: 1,
            editable: false
        },
        {
            field: 'description',
            headerName: t('Description'),
            minWidth: 400,
            flex: 1,
            editable: false
        },
        {
            field: '',
            headerName: '',
            minWidth: 160,
            display: 'flex',
            align: 'center',
            editable: false,
            sortable: false,
            renderCell: (params) => {
                return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5, pr: '6px' }, children: [_jsx(IconButton, { onClick: () => {
                                navigate(`/detail-asset/${id}/live-feed/${params.row.systemId}`);
                            }, title: 'View Live Feed', children: _jsx(VideoFilled, { fontSize: 20, color: theme.palette.success.main }) }), _jsx(IconButton, { onClick: () => {
                                setItemToDelete(params.row);
                                setIsConfirmDialogOpen(true);
                            }, children: _jsx(DeleteRegular, { fontSize: 20, color: theme.palette.error.main }) })] }));
            }
        }
    ];
    const handleShowAddSystemsDialog = () => {
        setSystemDialog((prev) => ({ ...prev, show: true }));
    };
    const handleCloseAddSystemsDialog = () => {
        setSystemDialog((prev) => ({ ...prev, show: false }));
    };
    return (_jsxs(Card, { className: 'assets-grid-card', sx: systemsCardStyles, children: [_jsx(Box, { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', children: _jsx(Button, { color: 'inherit', variant: 'contained', startIcon: _jsx(AddFilled, { fontSize: 20 }), onClick: handleShowAddSystemsDialog, children: t('AddSystem') }) }), _jsx(Box, { className: 'table-container', children: _jsx(DataGrid, { rows: rows, columns: columns, disableColumnMenu: true, disableColumnResize: true, disableColumnSelector: true }) }), systemDialog.show && (_jsx(SystemDialog, { isOpen: systemDialog.show, onClose: handleCloseAddSystemsDialog })), selectedSystemId && (_jsx(SystemDialog, { onClose: () => {
                    setIsEdit(false);
                    setSelectedSystemId(null);
                    dispatch(resetSystemEditMode());
                }, isOpen: isEdit, variant: 'edit', selectedSystemId: selectedSystemId, onSystemUpdated: handleSystemUpdated })), isConfirmDialogOpen && (_jsx(ConfirmDialog, { isOpen: isConfirmDialogOpen, onClose: () => setIsConfirmDialogOpen(false), onConfirm: handleDelete, loading: isDeleting, actionText: t('DeleteSystemText'), screenName: itemToDelete ? itemToDelete.systemName : '' }))] }));
};
export default Systems;
